import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Loading from '../../components/ui/Loading';
import ErrorMessage from '../../components/ui/ErrorMessage';
import Image from '../../components/ui/Image';
import Button from '../../components/ui/Button';
import { getRegistryInfoById } from '../../services/api/copyright';
import { getFullAttachmentUrl, processRichTextImages } from '../../services/api/index.js';

const CopyrightProductDetailPage = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProductDetail = async () => {
      try {
        setLoading(true);
        setError(null);
        // 调用接口获取产品详情
        const response = await getRegistryInfoById(id);
        // 转换数据结构以匹配现有组件
        const adaptedProduct = {
          id: response.registerId || response.id,
          title: response.workName || response.title,
          author: response.owner || response.author,
          phone: response.phone,
          content: response.workDesc || response.content,
          coverImage: getFullAttachmentUrl(response.coverId)
        };
        setProduct(adaptedProduct);
      } catch (err) {
        console.error('获取产品详情失败:', err);
        setError('获取产品数据失败');
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetail();
  }, [id]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('zh-CN');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <ErrorMessage
          message={error}
        />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12 text-gray-500">
          {t('copyright.products.notFound', '产品信息不存在')}
        </div>
      </div>
    );
  }

  // 处理富文本内容中的图片路径
  const processedContent = processRichTextImages(product.content);

  return (
    <div className="container mx-auto px-4 py-8">
      {/* 返回按钮 */}
      <div className="mb-6">
        <Button
          variant="outline"
          onClick={() => window.history.back()}
          className="text-gray-600 border-gray-300 hover:bg-gray-100"
        >
          ← {t('common.backToList', '返回')}
        </Button>
      </div>

      {/* 产品详情 */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-8">
        <div className="p-8">
          {/* 产品标题 */}
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {product.title}
          </h1>

          {/* 产品图片 - 自适应大小 */}
          <div className="product-image-section mb-8">
            <div className="max-w-sm">
              <Image
                src={product.coverImage}
                alt={product.title}
                className="w-full h-auto bg-gray-100 rounded-md shadow-md"
                imgClassName="w-full h-full object-cover"
                fallback="/images/default-product.jpg"
              />
            </div>
          </div>

          {/* 版权信息 */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              版权信息
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* 版权所有者 */}
              <div>
                <p className="text-sm text-gray-500">版权所有者</p>
                <p className="text-gray-900">{product.author}</p>
              </div>

              {/* 联系方式 */}
              <div>
                <p className="text-sm text-gray-500">联系方式</p>
                <p className="text-gray-900">{product.phone}</p>
              </div>
            </div>
          </div>

          {/* 产品描述 */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              产品描述
            </h2>
            <div
              className="text-gray-700 prose max-w-none"
              dangerouslySetInnerHTML={{ __html: processedContent }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CopyrightProductDetailPage;
